import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useRoleEntryScreen } from './hooks/useRoleEntryScreen'
import { appToast } from '../../../components/AppToast'
import { useAuth } from '../../../hooks/useAuth'
import AppDropdown from '../../../components/AppDropdown'
import RoleCapabilityBadge from '../../../components/RoleCapabilityBadge'
import PrivilegesMatrixTree from './components/PrivilegesMatrixTree'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

export default function RoleConfigurationScreen() {
  const { state, actions } = useRoleEntryScreen()
  const { hasPrivilege } = useAuth()
  const canSave = state.isEditMode ? hasPrivilege('RoleEdit') : hasPrivilege('RoleAdd')

  const roleOptions = useMemo(
    () =>
      state.availableRoles
        .filter((r) => !state.isEditMode || Number(r.id) !== Number(state.roleId))
        .map((r) => ({
          value: String(r.id),
          label: `${r.name} (${r.role_code})`,
          icon: 'admin_panel_settings',
        })),
    [state.availableRoles, state.isEditMode, state.roleId]
  )

  const handleCloneRole = (roleId) => {
    if (!roleId) return
    const templateRole = state.availableRoles.find((r) => String(r.id) === String(roleId))
    actions.cloneFromRole(roleId)
    if (templateRole) {
      appToast.success(`Copied permissions and scope from "${templateRole.name}"`)
    }
  }

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  return (
    <div className="mx-auto flex w-full max-w-[1550px] flex-col gap-7 pb-12">
      {/* ── Page Header ── */}
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={state.returnTo}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary hover:underline"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              Roles Directory
            </Link>
          </div>
          <h1 className="text-4xl font-extrabold tracking-[-0.02em] text-on-surface">Security &amp; Role Configuration</h1>
          <p className="mt-2 text-base leading-relaxed text-on-surface-variant">
            Define granular permissions and operational scope per role — covering View, Create, Edit, Delete, Archive, Approve, Export, and Import rights for every module and sub-module.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {roleOptions.length > 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low/40 px-3 py-1.5 shadow-sm">
              <span className="material-symbols-outlined text-base text-on-surface-variant">content_copy</span>
              <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant whitespace-nowrap">
                Clone Rights:
              </span>
              <AppDropdown
                value=""
                onChange={handleCloneRole}
                options={roleOptions}
                placeholder="Select role template..."
                disabled={state.isLoading || state.isSaving}
                className="w-60"
                triggerClassName="h-8 !py-0 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 text-xs font-semibold text-on-surface shadow-none hover:border-primary/40 hover:bg-surface-container-lowest"
                menuClassName="min-w-[240px]"
                align="right"
              />
            </div>
          ) : null}

          {state.isEditMode && hasPrivilege('RoleAdd') ? (
            <button
              type="button"
              onClick={actions.handleClone}
              disabled={state.isSaving || state.isLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-sm font-semibold text-on-surface hover:bg-surface-container-high transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">file_copy</span>
              Duplicate
            </button>
          ) : null}
        </div>
      </section>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} queueRoute="/approval/masters/list?module=roles" />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-20 text-center text-sm text-on-surface-variant flex flex-col items-center justify-center gap-3">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Loading RBAC configuration &amp; taxonomy tree...</span>
        </div>
      ) : (
        <>
          {/* ── Role Identity & Scope Header Cards ── */}
          <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
            {/* Core Identity */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-7 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-on-surface-variant">Core Identity &amp; Scope</p>
                <div className="flex items-center gap-2">
                  <RoleCapabilityBadge capability={state.roleCapability} />
                  <span className="rounded-full bg-surface-container-low px-2.5 py-0.5 text-xs font-mono font-bold text-primary">
                    {state.form.roleCode || 'NEW_ROLE'}
                  </span>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="grid gap-1.5">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Role Name <span className="text-error">*</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.name}
                    onChange={(event) => actions.patchForm('name', event.target.value)}
                    placeholder="e.g. Project Lead / HR Manager"
                    className="w-full rounded-lg border border-outline-variant/30 bg-surface-container-low/40 px-3.5 py-2.5 text-sm text-on-surface outline-none focus:border-primary/60 focus:bg-surface-container-lowest transition-all"
                  />
                </label>

                <label className="grid gap-1.5">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Role Code</span>
                  <input
                    type="text"
                    value={state.form.roleCode}
                    readOnly
                    placeholder="Auto-generated on creation"
                    className="w-full rounded-lg border border-outline-variant/20 bg-surface-container-low/25 px-3.5 py-2.5 text-sm text-on-surface-variant font-mono outline-none"
                  />
                </label>
              </div>

              <label className="mt-5 grid gap-1.5">
                <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Role Description</span>
                <textarea
                  rows={2}
                  value={state.form.description}
                  onChange={(event) => actions.patchForm('description', event.target.value)}
                  placeholder="Describe functional responsibilities and scope of this role..."
                  className="w-full resize-none rounded-lg border border-outline-variant/30 bg-surface-container-low/40 px-3.5 py-2.5 text-sm text-on-surface outline-none focus:border-primary/60 focus:bg-surface-container-lowest transition-all"
                />
              </label>
            </article>

            {/* Lifecycle & Status */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-7 shadow-sm flex flex-col justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-on-surface-variant">Lifecycle Status</p>

                <div className="mt-5 flex items-center gap-3">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl ${state.operationalStatus === 'active'
                        ? 'bg-emerald-500/15 text-emerald-600'
                        : 'bg-surface-variant text-on-surface-variant'
                      }`}
                  >
                    <span className="material-symbols-outlined text-2xl">
                      {state.operationalStatus === 'active' ? 'verified_user' : 'pause_circle'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-on-surface capitalize">{state.operationalStatus}</p>
                    <p className="text-xs text-on-surface-variant mt-0.5">
                      {state.isEditMode ? 'Role state across directory' : 'Initial assignment state'}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('active')}
                    className={`rounded-lg py-2 text-xs font-bold transition-colors ${state.operationalStatus === 'active'
                        ? 'bg-primary text-on-primary shadow-sm'
                        : 'border border-outline-variant/30 bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-low'
                      }`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('inactive')}
                    className={`rounded-lg py-2 text-xs font-bold transition-colors ${state.operationalStatus === 'inactive'
                        ? 'bg-error text-on-error shadow-sm'
                        : 'border border-outline-variant/30 bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-low'
                      }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>

              {/* Coverage Metric */}
              <div className="mt-6 rounded-xl border border-outline-variant/20 bg-surface-container-low/40 p-3.5">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-on-surface-variant">Coverage</span>
                  <span className="text-primary">{state.securityLevelPercent}%</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-surface-container-high overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${Math.max(4, state.securityLevelPercent)}%` }}
                  />
                </div>
                <p className="text-[11px] text-on-surface-variant mt-2 text-center">
                  {state.selectedPrivilegeCount} of {state.totalPrivilegeCount} rights enabled
                </p>
              </div>
            </article>
          </section>

          {/* ── Enterprise Dynamic Privileges Matrix Tree ── */}
          <PrivilegesMatrixTree
            tree={state.tree}
            selectedIds={state.selectedPrivilegeIds}
            roleCapability={state.roleCapability}
            onToggleAction={actions.toggleAction}
            onToggleSubmodule={actions.toggleSubmodule}
            onToggleModule={actions.toggleModule}
            onSelectAll={actions.selectAllPrivileges}
            onClearAll={actions.clearAllPrivileges}
            onSelectDefaults={actions.selectDefaultPrivileges}
            onSelectViewOnly={actions.selectViewOnlyPrivileges}
            onSelectViewApprove={actions.selectViewApprovePrivileges}
            onStripWrites={actions.stripWritePrivileges}
          />

          {/* ── Bottom Action Controls ── */}
          <div className="flex flex-col gap-3 rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 sm:flex-row sm:items-center sm:justify-between shadow-sm">
            <p className="text-xs text-on-surface-variant">
              Changes remain pending and non-operational until an authorized administrator approves them.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={actions.handleCancelChanges}
                className="rounded-lg border border-outline-variant/30 px-5 py-2 text-sm font-semibold text-on-surface hover:bg-surface-container-low transition-colors"
              >
                Reset Form
              </button>
              {canSave && (
                <button
                  type="button"
                  onClick={actions.handleSave}
                  disabled={state.isSaving || Boolean(state.pendingApproval)}
                  className="rounded-lg bg-primary px-7 py-2 text-sm font-bold text-on-primary shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
